import {
  Box,
  CardMedia,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Typography,
} from "@mui/material";
import React from "react";
import policy from "../../../data/privacy_policy.pdf";

const PrivacyModal = ({ open, onClose, acceptPrivacyHandler }) => {
  return (
    <Dialog open={open} maxWidth={"md"} fullWidth={true} onClose={onClose}>
      <DialogContent>
        <Box
          display="flex"
          flexDirection={"column"}
          gap={3}
          alignItems={"center"}
          marginBottom={3}
        >
          <Typography variant="title">RAWDAT.AI</Typography>
          <Typography variant="h5" fontWeight={700}>
            PRIVACY POLICY
          </Typography>
          <Box sx={{ "& h1, h2, h3": { margin: "1rem 0" } }}>
            <p>
              Rawdat.AI (“Rawdat.AI,” “Company,” “We,” “Our”) respects your
              privacy and is committed to protecting it through our compliance
              with this policy. This document details the types of information
              we may collect or that you may provide when you access, use, or
              register with Rawdat.AI’s website or online platform (“Services”)
              and our practices for using, maintaining, protecting, and
              disclosing that information.
            </p>
            <p>
              Please read this carefully to understand our policies and
              practices regarding your information and how we will treat it. By
              using or registering with our Services, you agree to this Privacy
              Policy. Your continued use of our Services after we make changes
              is deemed to be acceptance of those changes. We encourage each
              user to check the policy periodically for updates as it may change
              from time to time.
            </p>

            <h3>Information We Collect and How We Collect It</h3>
            <p>We collect information from and about users of our Services:</p>
            <ul>
              <li>
                When the information is directly provided to Rawdat.AI by the
                user
              </li>
              <li>Automatically when you use the Services</li>
            </ul>
            <h3>Information You Provide to Us</h3>
            <p>
              When you use or register with the Services, we may ask you to
              provide information by which you may be personally identified,
              such as your name, email address, location, photograph, employment
              information, financial payment information, and any other
              identifier by which you may be contacted or identified online or
              offline ("Personal Information").
            </p>
            <p>This information includes:</p>
            <ul>
              <li>
                Information that you provide by filling in the forms in the
                Services. This includes information provided at the time of
                registering your profile on Rawdat.AI
              </li>
              <li>
                Details of transactions you carry out through the Services and
                of the fulfillment of your orders and requests.
              </li>
              <li>
                Records and copies of your correspondence (including e-mail
                addresses and phone numbers), if you contact us. We may also ask
                you for information when you report a problem with the Services.
              </li>
              <li>
                Your responses to surveys that we might ask you to complete for
                research and development purposes.
              </li>
            </ul>
            <h3>Automatic Information Collection</h3>
            <p>
              When you download, access, or use the Services, it may use
              technology to automatically collect:
            </p>
            <ul>
              <li>
                Usage Details. When you access and use the Services, we may
                automatically collect certain details of your access to and use
                of the Services, including traffic data, location data, logs and
                other communication data and the resources that you access and
                use on or through the Services.
              </li>
              <li>
                Device Information. We may collect information about your
                computer, mobile device, and internet connection, including the
                computer or mobile device's unique device identifier, IP
                address, operating system, browser type, mobile network
                information and the device's telephone number.
              </li>
            </ul>
            <h3>Local Storage, Cookies, Web Beacons, and Tracking</h3>
            <p>
              Our Services use local storage and cookies, which are small text
              files that are intended to make the Services better for you to
              use. In general, cookies are used to retain preferences, store
              information for things like shopping carts, and provide tracking
              data to third-party applications like Google Analytics.
            </p>
            <p>
              We may also employ software technology known as “web beacons” or
              “clear GIFs,” which helps us keep track of what content on our
              service is effective. Web beacons are small graphics with a unique
              identifier that are used to track the online movements of Internet
              users. Web beacons are embedded in the web pages you review, so
              they are not stored on your hard drive. The web beacons we may use
              will not track or collect any personally identifiable information
              about you and they are in no way linked to your personally
              identifiable information.
            </p>
            <p>
              The Services also use third-party analysis and tracking services
              to track the performance of our services, understand how you use
              our services, and offer you an improved and safer experience. Such
              usage information will be deleted as soon as this information is
              no longer required for the purpose collected, and will not be
              shared with third parties until after being anonymized.
            </p>
            <h3>Use of Personal Information We Collect</h3>
            <p>
              We may use the information we collect from You in the following
              ways:
            </p>
            <ul>
              <li>To set up your account with Rawdat.AI</li>
              <li>
                To perform transactions on your behalf through the Services and
                third-party service providers.
              </li>
              <li>
                To personalize your user experience, and to allow us to deliver
                the type of content and offerings in which you are most
                interested.
              </li>
              <li>
                To deliver service messages and other services and content you
                request and send information related to accounts and services,
                including confirmations, invoices, technical notices, updates,
                security alerts, support and administrative messages, and to
                enable user-to-user communication.
              </li>
              <li>
                To conduct an aggregated analysis of the performance of our
                Services, including referral data if you arrive at our website
                from an external source.
              </li>
            </ul>
            <p>
              All the data we collect at Rawdat.AI is necessary for us to
              deliver the services you use. The amount we collect has been
              minimized wherever possible to respect your privacy.
            </p>
            <h3>How We May Share Information We Collect</h3>
            <ul>
              <li>
                To provide to third party service providers that perform
                services on our behalf, such as third-party accounting or
                payment platforms.
              </li>
              <li>
                We may share personal information when we do a business deal, or
                negotiate a business deal, involving the sale or transfer of all
                or a part of our business or assets. These deals can include any
                merger, financing, acquisition, or bankruptcy transaction or
                proceeding. However, this information will be in the aggregate
                and anonymized to protect your personal information.
              </li>
              <li>
                We may share information to respond to lawful requests and legal
                processes.
              </li>
              <li>
                We may share information to protect the rights and property of
                Rawdat.AI, our agents, customers, and others. This includes
                enforcing our agreements, policies, and terms of use.
              </li>
              <li>
                We may share information in an emergency. This includes
                protecting the safety of our employees and agents, our
                customers, or any person.
              </li>
              <li>
                We may share information with those who need it to do work for
                us.
              </li>
            </ul>
            <p>
              We do not sell, trade, or otherwise transfer to outside parties
              your personally identifiable information.
            </p>
            <h3>Data Retention</h3>
            <p>
              We retain personal information we collect from you where we have
              an ongoing legitimate business need to do so (for example, to
              provide you with a service you have requested or to comply with
              applicable legal, tax, or accounting requirements).
            </p>
            <p>
              When we have no ongoing legitimate business need to process
              personal information, we will either delete or anonymize it or, if
              this is not possible (for example, because personal information
              has been stored in backup archives), then we will securely store
              personal information and isolate it from any further processing
              until deletion is possible.
            </p>
            <h3>Security of Your Personal Information</h3>
            <p>
              The security of your Personal Information is important to us. When
              you enter sensitive information (such as credit card number) on
              our Services, we encrypt that information using secure socket
              layer technology (SSL).
            </p>
            <p>
              Rawdat.AI takes reasonable security measures to protect your
              Personal Information to prevent loss, misuse, unauthorized access,
              disclosure, alteration, and destruction. Please be aware, however,
              that despite our efforts, no security measures are impenetrable.
            </p>
            <p>
              If you use a password on the Services, you are responsible for
              keeping it confidential. We recommend not sharing your password
              with other users, and if shared, the responsibility lies with the
              primary user. If you believe your password has been compromised,
              please notify us immediately.
            </p>
            <h3>Legal Basis for Processing Personal Information</h3>
            <p>
              Our legal basis for collecting and using the personal information
              described above will depend on the personal information concerned
              and the specific context in which we collect it.
            </p>
            <p>
              However, we will normally collect personal information from you
              only (i) where we need the personal information to perform a
              contract with you; (ii) where the processing is in our legitimate
              interests and not overridden by your rights; or (iii) where we
              have your consent to do so. We have a legitimate interest in
              operating our Services and communicating with you as necessary to
              provide these Services, for example when responding to your
              queries, improving our platform, undertaking marketing, or for the
              purposes of detecting or preventing illegal activities.
            </p>
            <p>
              In some cases, we may also have a legal obligation to collect
              personal information from you or may otherwise need the personal
              information to protect your vital interests or those of another
              person.
            </p>
            <p>
              If we ask you to provide personal information to comply with a
              legal requirement or to perform a contract with you, we will make
              this clear at the relevant time and advise you whether the
              provision of your personal information is mandatory or not (as
              well as of the possible consequences if you do not provide your
              personal information).
            </p>
            <h3>
              Your Data Protection Rights Under the General Data Protection
              Regulation (GDPR)
            </h3>
            <p>
              If you or a user are a resident of the EEA, you have the following
              data protection rights:
            </p>
            <ul>
              <li>
                You may access, correct, update, or request deletion of your
                personal information.
              </li>
              <li>
                In addition, you can object to the processing of your personal
                information, ask us to restrict the processing of your personal
                information, or request portability of your personal
                information.
              </li>
              <li>
                You have the right to opt-out of marketing communications we
                send you at any time. You can exercise this right by clicking on
                the "unsubscribe" or "opt-out" link in the marketing emails we
                send you.
              </li>
              <li>
                Similarly, if we have collected and processed your personal
                information with your consent, then you can withdraw your
                consent at any time. Withdrawing your consent will not affect
                the lawfulness of any processing we conducted prior to your
                withdrawal, nor will it affect the processing of your personal
                information conducted in reliance on lawful processing grounds
                other than consent.
              </li>
              <li>
                You have the right to complain to a data protection authority
                about our collection and use of your personal information. For
                more information, please contact your local data protection
                authority.
              </li>
            </ul>
            <p>
              We respond to all requests we receive from individuals wishing to
              exercise their data protection rights in accordance with
              applicable data protection laws. To exercise any of these rights,
              please contact us at support@rawdat.ai.
            </p>
            <h3>
              Your Data Protection Rights under the California Consumer Privacy
              Act (CCPA)
            </h3>
            <p>
              The California Consumer Privacy Act (“CCPA”) provides consumers
              with specific rights regarding their Personal Information. If you
              are a California resident, you have the right to:
            </p>
            <ul>
              <li>
                Request we disclose to you free of charge the following
                information covering the 12 months preceding your request: (a)
                the categories of Personal Information about you that we
                collected; (b) the categories of sources from which the Personal
                Information was collected; (c) the purpose for collecting
                Personal Information about you; (d) the categories of third
                parties to whom we disclosed Personal Information about you and
                the categories of Personal Information that was disclosed (if
                applicable) and the purpose for disclosing the Personal
                Information about you; and (e) the specific pieces of Personal
                Information we collected about you;
              </li>
              <li>
                Request we delete Personal Information we collected from you,
                unless the CCPA recognizes an exception;
              </li>
              <li>
                If the business sells Personal Information, you have a right to
                opt-out of that sale. Consistent with California law, if you
                choose to exercise your applicable CCPA rights, we will not
                charge you different prices or provide you a different quality
                of services. If we ever offer a financial incentive or product
                enhancement that is contingent upon you providing your Personal
                Information, we will not do so unless the benefits to you are
                reasonably related to the value of the Personal Information that
                you provide to us. If you are a California resident and wish to
                exercise your rights under the CCPA, please contact us at
                support@rawdat.ai.
              </li>
            </ul>
            <h3>Updating and Accessing Your Personal Information</h3>
            <p>
              If your Personal Information changes, we invite you to correct or
              update your information. We will retain your information for as
              long as your account is active or as needed to provide you
              services. If you wish to cancel your account, request that we no
              longer use your information to provide you services, or delete
              your Personal Information, you may do so through your account
              settings, or contact us at support@rawdat.ai. We will respond to
              your request to have your Personal Information updated as soon as
              possible. We will retain and use your information as necessary to
              comply with our legal obligations, resolve disputes, and enforce
              our agreements.
            </p>
            <h3>Choices About Use of Your Information</h3>
            <p>
              You can request that your account be deleted, however, certain
              financial record-keeping information may be maintained in our
              systems because we have a legitimate legal and financial interest
              in maintaining such records.
            </p>
            <p>
              You may unsubscribe from receiving certain promotional emails from
              us. If you wish to do so, simply follow the instructions found at
              the end of the email. Even if you unsubscribe, we may still
              contact you for informational, transactional, account-related, or
              similar purposes, including messages about your current Rawdat.AI
              account and services.
            </p>
            <p>
              Many browsers have an option for disabling cookies, which may
              prevent your browser from accepting new cookies or enable
              selective use of cookies. Please note that, if you choose not to
              accept cookies, some features and the personalization of our
              Services may no longer work for you. You will continue to receive
              advertising material, but it will not be tailored to your
              interests.
            </p>
            <h3>Third-Party Disclosure and Links</h3>
            <p>
              The Services may include links to other websites whose privacy
              practices may differ from those of Rawdat.AI. If you submit
              Personal Information to any of those sites, your information is
              governed by their privacy statements. We encourage you to
              carefully read the privacy statement of any third-party websites
              you visit.
            </p>
            <h3>Notice Concerning The Information Of Children</h3>
            <p>
              Rawdat.AI is not directed to children under the age of 13 and does
              not intentionally collect any information from children under the
              age of 13. Please contact us if your child has provided Personal
              Information to us and we will delete the information.
            </p>
            <h3>Changes to This Policy</h3>
            <p>
              Rawdat.AI may change this Policy from time to time. If we make any
              changes to this Policy, we will change the "Last Updated" date
              below and may notify you by email (sent to the e-mail address
              specified in your account). We encourage you to review this Policy
              whenever you visit the Services to understand how your Personal
              Information is used.
            </p>
            <h3>Questions About This Policy</h3>
            <p>
              If you have any questions about this Policy, our practices related
              to the Services, or if you would like to have us remove your
              information from our database, please feel free to contact us at
              support@rawdat.ai.
            </p>
            <p>Last Update: August 29, 2023</p>
          </Box>
        </Box>

        {/* <iframe className="Checking" src={`${policy}#toolbar=0`} width={"100%"} height={600} /> */}
        <Divider sx={{ mt: 2 }} />
      </DialogContent>
      <DialogActions
        sx={{
          display: "flex",
          gap: "0.75rem",
          justifyContent: "flex-start",
          padding: "1.5rem 1.5rem 1.5rem",
        }}
      >
        <Chip
          variant="outlined"
          color="primary"
          label="Close"
          onClick={onClose}
        />

        <Chip
          variant="filled"
          color="primary"
          label={"I have read and accept the Privacy policy"}
          onClick={() => {
            acceptPrivacyHandler();
            onClose();
          }}
        />
      </DialogActions>
    </Dialog>
  );
};

export default PrivacyModal;
